import {useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";


function TranscriptAutocomplete({label, defaultValue, onChange}) { // strict validation + uppercase
    const [input, setInput] = useState((defaultValue ?? "").toUpperCase());
    const [open, setOpen] = useState(false);
    const [hoverIdx, setHoverIdx] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [transcripts, setTranscripts] = useState(null); // stocké en MAJUSCULE
    const [interacted, setInteracted] = useState(false);
    const [valid, setValid] = useState(false);
    const debounceRef = useRef();
    const lastCommittedRef = useRef((defaultValue ?? "").toUpperCase());

    // Build a safe relative URL to data/
    const DATA_URL = `${process.env.PUBLIC_URL || ""}/data/transcripts.json`;

    // Lazy-load the transcript list once the user starts typing (>=2 chars)
    useEffect(() => {
        if (transcripts !== null || input.trim().length < 2) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await fetch(DATA_URL, {cache: "no-store"});
                const ct = res.headers.get("content-type") || "";
                if (!res.ok || !ct.includes("application/json")) {
                    if (!cancelled) setTranscripts([]);
                    return;
                }
                const arr = await res.json();
                const cleaned = Array.isArray(arr)
                    ? Array.from(new Set(arr.map(String)))
                        .map(s => s.replace(/^.*[\\/]/, ""))  // basename
                        .map(s => s.replace(/\.[^.]+$/, ""))  // strip extension
                        .map(s => s.toUpperCase())
                        .sort()
                    : [];
                if (!cancelled) setTranscripts(cleaned);
            } catch {
                if (!cancelled) setTranscripts([]); // fail quietly in prod
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [input, transcripts, DATA_URL]);

    // Debounce input for filtering
    const [debounced, setDebounced] = useState(input);
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebounced(input), 150);
        return () => clearTimeout(debounceRef.current);
    }, [input]);

    // Build matches: prefer "<q>-" (anchored), then prefix, then substring
    const matches = useMemo(() => {
        const list = transcripts || [];
        const q = debounced.trim();
        if (!q || list.length === 0) return [];
        const hyphenPrefix = (q + "-");
        const strict = [], prefix = [], infix = [];
        let scanned = 0, SCAN_LIMIT = 2000, COLLECT_LIMIT = 200;
        for (const s of list) {
            if (s.startsWith(hyphenPrefix)) strict.push(s);
            else if (s.startsWith(q)) prefix.push(s);
            else if (s.includes(q)) infix.push(s);
            scanned++;
            if (scanned >= SCAN_LIMIT && (strict.length + prefix.length + infix.length) >= COLLECT_LIMIT) break;
        }
        const ordered = strict.length ? strict : [...prefix, ...infix];
        return ordered.slice(0, 200);
    }, [debounced, transcripts]);

    // Do we have any anchored suggestions like "SIRT1-…"?
    const anchoredSome = useMemo(() => {
        const q = debounced.trim();
        if (!q || matches.length === 0) return false;
        return matches.some(m => m.startsWith(q + "-"));
    }, [debounced, matches]);

    // Validité stricte
    useEffect(() => {
        if (!transcripts || !input.trim()) { setValid(false); return; }
        const up = input.trim();
        setValid(transcripts.some(t => t === up));
    }, [transcripts, input]);

    // Open/close dropdown (only when “almost complete”)
    useEffect(() => {
        if (!interacted) { setOpen(false); setHoverIdx(-1); return; }
        const q = debounced.trim();
        const hasInput = debounced.length >= 3; // seuil choisi
        const hasMatches = matches.length > 0;
        const identicalSingle = matches.length === 1 && matches[0] === q;
        const smallEnough = matches.length <= 200;
        const shouldOpen = hasInput && hasMatches && !identicalSingle && (smallEnough || anchoredSome);
        setOpen(shouldOpen);
        setHoverIdx(-1);
    }, [interacted, debounced, matches.length, anchoredSome, matches]);

    const commit = (val) => {
        const v = (val ?? input).trim();
        lastCommittedRef.current = v;
        onChange?.(v);
    };

    const pick = (s) => { setInput(s); setInteracted(true); commit(s); setOpen(false); };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            if (open && matches.length && hoverIdx >= 0) { e.preventDefault(); pick(matches[hoverIdx]); return; }
            e.preventDefault(); commit(); setOpen(false);
        } else if (e.key === "ArrowDown") { if (!open && matches.length) setOpen(true); e.preventDefault(); setHoverIdx(i => (i + 1) % Math.max(matches.length, 1)); }
        else if (e.key === "ArrowUp") { if (!open && matches.length) setOpen(true); e.preventDefault(); setHoverIdx(i => (i - 1 + Math.max(matches.length, 1)) % Math.max(matches.length, 1)); }
        else if (e.key === "Escape") { setOpen(false); }
    };

    return (
        <Box sx={{position: "relative", maxWidth: 540, mb: 2}}>
            <TextField
                label={label}
                value={input}
                onChange={e => { setInput(e.target.value.toUpperCase()); setInteracted(true); }}
                onFocus={() => { if (interacted && matches.length) setOpen(true); }}
                onBlur={() => { commit(); setTimeout(() => setOpen(false), 120); }}
                onKeyDown={onKeyDown}
                fullWidth
                size="small"
                variant="outlined"
                error={!!input && !valid}
                helperText={!!input && !valid ? "Select a valid transcript from list" : ""}
                required
                inputProps={{style:{textTransform:'uppercase'}}}
            />
            {loading && (
                <Box sx={{fontSize: 12, mt: 1, opacity: 0.7, display: "flex", alignItems: "center"}}>
                    <CircularProgress size={16} sx={{mr: 1}}/> Loading transcripts…
                </Box>
            )}
            {open && matches.length > 0 && (
                <Paper sx={{position: "absolute", zIndex: 10, mt: 1, width: "100%", maxHeight: 240, overflowY: "auto"}}>
                    <List>
                        {matches.map((m, idx) => (
                            <ListItem disablePadding key={m}>
                                <ListItemButton
                                    selected={idx === hoverIdx}
                                    onMouseDown={e => { e.preventDefault(); pick(m); }}
                                    onMouseEnter={() => setHoverIdx(idx)}
                                >
                                    <ListItemText primary={m}/>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );

}

export default TranscriptAutocomplete;
